import React, { useState } from 'react';
import { Info } from 'lucide-react';
import { PlanCards } from '../components/PlanCards';
import { ConfirmationModal } from '../components/ConfirmationModal';
import '../components/ConfirmationModal.css';
import { supabase } from '../lib/supabaseClient';
import './SettingsPage.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';

const TIER_DETAILS = {
    free: {
        perks: [
            { text: "Unlimited Free Video Downloads", tooltip: "Download videos from 1800+ sites at highest available quality" },
            { text: "Unlimited Free Audio Downloads", tooltip: "Download audio from 1800+ sites at highest available quality" },
            { text: "Unlimited Audio Conversions", tooltip: "Convert your audio from any format to MP3" },
            { text: "Smart Clips with Virality Score", tooltip: "AI-analyzed clips ranked by viral potential" },
            { text: "AI Animated Captions", tooltip: "Achieve near-perfect captions with our latest AI. Choose from popular styles or create your own." },
            { text: "AI Smart Cropping", tooltip: "AI automatically detects faces to keep your main subject in frame, eliminating empty space." },
            { text: "Rant Style Videos", tooltip: "Create popular faceless 'Rant' style videos (like Roblox rants) in minutes." },
            { text: "Ranked Style Videos", tooltip: "Easily create 'Top 10' or ranked lists for any niche." },
            { text: "Quiz Style Videos", tooltip: "Quickly create engaging trivia/quiz videos complete with animations on trending topics." }
        ]
    },
    creator: {
        perks: [
            { text: "Unlimited Free Video Downloads", tooltip: "Download videos from 1800+ sites at highest available quality" },
            { text: "Unlimited Free Audio Downloads", tooltip: "Download audio from 1800+ sites at highest available quality" },
            { text: "Unlimited Audio Conversions", tooltip: "Convert your audio from any format to MP3" },
            { text: "Smart Clips with Virality Score", tooltip: "AI-analyzed clips ranked by viral potential" },
            { text: "AI Animated Captions", tooltip: "Achieve near-perfect captions with our latest AI. Choose from popular styles or create your own." },
            { text: "AI Smart Cropping", tooltip: "AI automatically detects faces to keep your main subject in frame, eliminating empty space." },
            { text: "Rant Style Videos", tooltip: "Create popular faceless 'Rant' style videos (like Roblox rants) in minutes." },
            { text: "Ranked Style Videos", tooltip: "Easily create 'Top 10' or ranked lists for any niche." },
            { text: "Quiz Style Videos", tooltip: "Quickly create engaging trivia/quiz videos complete with animations on trending topics." },
            { text: "Priority Support" }
        ]
    },
    pro: {
        perks: [
            { text: "Unlimited Free Video Downloads", tooltip: "Download videos from 1800+ sites at highest available quality" },
            { text: "Unlimited Free Audio Downloads", tooltip: "Download audio from 1800+ sites at highest available quality" },
            { text: "Unlimited Audio Conversions", tooltip: "Convert your audio from any format to MP3" },
            { text: "Smart Clips with Virality Score", tooltip: "AI-analyzed clips ranked by viral potential" },
            { text: "AI Animated Captions", tooltip: "Achieve near-perfect captions with our latest AI. Choose from popular styles or create your own." },
            { text: "AI Smart Cropping", tooltip: "AI automatically detects faces to keep your main subject in frame, eliminating empty space." },
            { text: "Rant Style Videos", tooltip: "Create popular faceless 'Rant' style videos (like Roblox rants) in minutes." },
            { text: "Ranked Style Videos", tooltip: "Easily create 'Top 10' or ranked lists for any niche." },
            { text: "Quiz Style Videos", tooltip: "Quickly create engaging trivia/quiz videos complete with animations on trending topics." },
            { text: "Priority Support" },
            { text: "Everything in Creator" }
        ]
    },
    studio: {
        perks: [
            { text: "Unlimited Free Video Downloads", tooltip: "Download videos from 1800+ sites at highest available quality" },
            { text: "Unlimited Free Audio Downloads", tooltip: "Download audio from 1800+ sites at highest available quality" },
            { text: "Unlimited Audio Conversions", tooltip: "Convert your audio from any format to MP3" },
            { text: "Smart Clips with Virality Score", tooltip: "AI-analyzed clips ranked by viral potential" },
            { text: "AI Animated Captions", tooltip: "Achieve near-perfect captions with our latest AI. Choose from popular styles or create your own." },
            { text: "AI Smart Cropping", tooltip: "AI automatically detects faces to keep your main subject in frame, eliminating empty space." },
            { text: "Rant Style Videos", tooltip: "Create popular faceless 'Rant' style videos (like Roblox rants) in minutes." },
            { text: "Ranked Style Videos", tooltip: "Easily create 'Top 10' or ranked lists for any niche." },
            { text: "Quiz Style Videos", tooltip: "Quickly create engaging trivia/quiz videos complete with animations on trending topics." },
            { text: "Dedicated Support" },
            { text: "Everything in Pro" }
        ]
    }
};

export const SettingsPage = ({ session, profile, tiers, refreshUsage }) => {
    const [billingInterval, setBillingInterval] = useState('month');
    const [portalLoading, setPortalLoading] = useState(false);
    const [buyingPackId, setBuyingPackId] = useState(null); // Track which credit pack is being purchased
    const [error, setError] = useState('');

    // Email change state
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [newEmail, setNewEmail] = useState('');
    const [emailLoading, setEmailLoading] = useState(false);
    const [emailSuccess, setEmailSuccess] = useState('');
    const [emailError, setEmailError] = useState('');

    // Password change state
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordLoading, setPasswordLoading] = useState(false);
    const [passwordSuccess, setPasswordSuccess] = useState('');
    const [passwordError, setPasswordError] = useState('');

    // Plan change modal state
    const [planModal, setPlanModal] = useState({
        isOpen: false,
        type: null, // 'upgrade' | 'downgrade' | 'success'
        tierKey: null,
        tierName: '',
        priceDisplay: '',
        intervalLabel: '',
        nextBillingDate: '',
        successMessage: ''
    });

    const handleManageSubscription = async () => {
        if (!session?.access_token) return;
        setPortalLoading(true);
        try {
            const res = await fetch(`${API_BASE}/api/billing/portal`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${session.access_token}` },
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Unable to open portal');
            window.location.assign(data.portal_url);
        } catch (err) {
            setError(err.message);
            setPortalLoading(false);
        }
    };

    const handleUpgrade = (tierKey) => {
        if (!session?.access_token) return;

        const targetTier = tiers[tierKey];
        const tierName = targetTier?.label || tierKey;
        const priceDisplay = billingInterval === 'year'
            ? targetTier?.amount_display_annual || targetTier?.amount_display
            : targetTier?.amount_display;
        const intervalLabel = billingInterval === 'year' ? 'year' : 'month';

        const nextBillingDate = profile?.usage?.resets_at
            ? new Date(profile.usage.resets_at).toLocaleDateString()
            : 'your next billing cycle';

        setPlanModal({
            isOpen: true,
            type: 'upgrade',
            tierKey,
            tierName,
            priceDisplay,
            intervalLabel,
            nextBillingDate,
            successMessage: ''
        });
    };

    const handleDowngrade = (tierKey) => {
        if (!session?.access_token) return;

        const targetTier = tiers[tierKey];
        const tierName = targetTier?.label || tierKey;
        const priceDisplay = billingInterval === 'year'
            ? targetTier?.amount_display_annual || targetTier?.amount_display
            : targetTier?.amount_display;
        const intervalLabel = billingInterval === 'year' ? 'year' : 'month';

        const nextBillingDate = profile?.usage?.resets_at
            ? new Date(profile.usage.resets_at).toLocaleDateString()
            : 'your next billing cycle';

        setPlanModal({
            isOpen: true,
            type: 'downgrade',
            tierKey,
            tierName,
            priceDisplay,
            intervalLabel,
            nextBillingDate,
            successMessage: ''
        });
    };

    const closePlanModal = () => {
        setPlanModal(prev => ({ ...prev, isOpen: false }));
    };

    const confirmPlanChange = async () => {
        const { type, tierKey } = planModal;

        if (type === 'upgrade') {
            try {
                const res = await fetch(`${API_BASE}/api/billing/checkout`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${session.access_token}`,
                    },
                    body: JSON.stringify({
                        tier: tierKey,
                        interval: billingInterval,
                        referral_id: window.promotekit_referral
                    }),
                });
                const data = await res.json();
                if (!res.ok) throw new Error(data.error || 'Unable to start checkout');
                window.location.assign(data.checkout_url);
            } catch (err) {
                setError(err.message);
                closePlanModal();
            }
        } else if (type === 'downgrade') {
            try {
                const res = await fetch(`${API_BASE}/api/billing/downgrade`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${session.access_token}`,
                    },
                    body: JSON.stringify({ tier: tierKey, interval: billingInterval }),
                });
                const data = await res.json();
                if (!res.ok) throw new Error(data.error || 'Unable to schedule downgrade');

                if (data.status === 'scheduled') {
                    const date = new Date(data.effective_date).toLocaleDateString();
                    setPlanModal(prev => ({
                        ...prev,
                        type: 'success',
                        successMessage: `Your plan will change to ${prev.tierName} on ${date}. You will continue to have access to your current plan until then.`
                    }));
                    refreshUsage();
                }
            } catch (err) {
                setError(err.message);
                closePlanModal();
            }
        } else if (type === 'success') {
            closePlanModal();
        }
    };

    const handleEmailChange = async (e) => {
        e.preventDefault();
        if (!supabase || !newEmail) return;

        setEmailLoading(true);
        setEmailError('');
        setEmailSuccess('');

        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(newEmail)) {
            setEmailError('Please enter a valid email address');
            setEmailLoading(false);
            return;
        }

        try {
            const { error } = await supabase.auth.updateUser({ email: newEmail });
            if (error) throw error;

            setEmailSuccess('Confirmation emails sent to both your current and new email addresses. Please check both inboxes to complete the change.');
            setNewEmail('');
            setShowEmailModal(false);
        } catch (err) {
            setEmailError(err.message || 'Failed to update email');
        } finally {
            setEmailLoading(false);
        }
    };

    const handlePasswordChange = async (e) => {
        e.preventDefault();
        if (!supabase || !newPassword) return;

        setPasswordLoading(true);
        setPasswordError('');
        setPasswordSuccess('');

        // Validate password length
        if (newPassword.length < 6) {
            setPasswordError('Password must be at least 6 characters long');
            setPasswordLoading(false);
            return;
        }

        // Validate passwords match
        if (newPassword !== confirmPassword) {
            setPasswordError('Passwords do not match');
            setPasswordLoading(false);
            return;
        }

        try {
            const { error } = await supabase.auth.updateUser({ password: newPassword });
            if (error) throw error;

            setPasswordSuccess('Password updated successfully');
            setNewPassword('');
            setConfirmPassword('');
            setShowPasswordModal(false);
        } catch (err) {
            setPasswordError(err.message || 'Failed to update password');
        } finally {
            setPasswordLoading(false);
        }
    };

    const CREDIT_PACKS = [
        { id: 'pack_500', credits: 500, price: 500, price_display: '$5.00' },
        { id: 'pack_1100', credits: 1100, price: 1000, price_display: '$10.00', bonus: '10% Bonus' },
        { id: 'pack_2500', credits: 2500, price: 2000, price_display: '$20.00', bonus: '25% Bonus' },
    ];

    const handleBuyCredits = async (packId) => {
        if (!session?.access_token) return;
        setBuyingPackId(packId); // Track which pack is loading
        setError('');
        try {
            const res = await fetch(`${API_BASE}/api/billing/buy-credits`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${session.access_token}`,
                },
                body: JSON.stringify({
                    pack_id: packId,
                    referral_id: window.promotekit_referral
                }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Unable to start checkout');
            window.location.assign(data.checkout_url);
        } catch (err) {
            setError(err.message);
            setBuyingPackId(null);
        }
    };

    const storageUsed = profile?.usage?.storage_used || 0;
    const storageLimit = profile?.usage?.storage_limit || 1; // bytes
    const storagePercent = Math.min(100, (storageUsed / storageLimit) * 100);

    const formatBytes = (bytes) => {
        if (bytes === 0) return '0 MB';
        const k = 1024;
        if (bytes < k * k * k) return `${(bytes / (k * k)).toFixed(0)} MB`; // < 1 GB -> MB
        return `${(bytes / (k * k * k)).toFixed(2)} GB`; // >= 1 GB
    };

    const storageDisplayIndex = formatBytes(storageUsed);
    const storageLimitDisplay = formatBytes(storageLimit);

    if (!profile) return <div>Loading...</div>;

    // Get current tier details and filter available tiers
    const currentTierKey = profile.usage.tier;
    const currentTierData = tiers ? tiers[currentTierKey] : null;
    const currentTierPerks = TIER_DETAILS[currentTierKey]?.perks || TIER_DETAILS['free']?.perks || [];

    // Create a new object for available tiers excluding the current one, and merging perks
    const availableTiers = tiers ? Object.fromEntries(
        Object.entries(tiers)
            .filter(([key]) => key !== currentTierKey)
            .map(([key, data]) => [key, {
                ...data,
                perks: TIER_DETAILS[key]?.perks || []
            }])
    ) : {};

    return (
        <div className="page-container settings-page">
            <div className="settings-header">
                <h1>Settings</h1>
            </div>
            {error && <p className="error">{error}</p>}

            <section className="settings-section">
                <h2>Credits & Usage</h2>
                <div className="settings-card" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>

                    {/* Balance Panel */}
                    <div style={{
                        background: 'linear-gradient(135deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 50%, rgba(255,255,255,0.04) 100%)',
                        backdropFilter: 'blur(12px)',
                        WebkitBackdropFilter: 'blur(12px)',
                        padding: '20px',
                        borderRadius: '16px',
                        border: '1px solid rgba(255,255,255,0.12)',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.08)'
                    }}>
                        <div style={{ fontSize: '0.9rem', color: '#888', marginBottom: '8px' }}>Available Balance</div>
                        <div style={{ fontSize: '2.5rem', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span>🪙</span>
                            {profile.usage.credits?.toLocaleString() || 0}
                        </div>
                        {profile.usage.monthly_credits > 0 && (
                            <div style={{ marginTop: '10px', fontSize: '0.85rem', color: '#666' }}>
                                Next refill: {profile.usage.monthly_credits} credits on {new Date(profile.usage.monthly_refill_at || profile.usage.resets_at).toLocaleDateString()}
                            </div>
                        )}
                    </div>

                    {/* Storage Panel */}
                    <div style={{
                        background: 'linear-gradient(135deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 50%, rgba(255,255,255,0.04) 100%)',
                        backdropFilter: 'blur(12px)',
                        WebkitBackdropFilter: 'blur(12px)',
                        padding: '20px',
                        borderRadius: '16px',
                        border: '1px solid rgba(255,255,255,0.12)',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.08)'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                            <div style={{ fontSize: '0.9rem', color: '#888' }}>Storage Used</div>
                            <div style={{ fontSize: '0.9rem', color: '#ccc' }}>{storageDisplayIndex} / {storageLimitDisplay}</div>
                        </div>
                        <div style={{
                            height: '12px',
                            background: 'rgba(255,255,255,0.1)',
                            borderRadius: '6px',
                            overflow: 'hidden',
                            marginBottom: '15px'
                        }}>
                            <div style={{
                                width: `${storagePercent}%`,
                                height: '100%',
                                background: storagePercent > 90 ? '#ef4444' : '#3b82f6',
                                borderRadius: '6px',
                                transition: 'width 0.5s ease'
                            }} />
                        </div>
                        <div style={{ fontSize: '0.85rem', color: '#666' }}>
                            Permanent storage for your projects and assets.
                        </div>
                    </div>
                </div>

                <h3 style={{ marginTop: '30px', marginBottom: '15px' }}>Top Up Credits</h3>
                <div className="credit-packs-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                    {CREDIT_PACKS.map(pack => {
                        const isLoading = buyingPackId === pack.id;
                        return (
                            <div key={pack.id} className="settings-card pack-card" style={{
                                textAlign: 'center',
                                cursor: isLoading ? 'wait' : 'pointer',
                                transition: 'transform 0.2s, border-color 0.2s',
                                position: 'relative',
                                overflow: 'hidden',
                                opacity: buyingPackId && !isLoading ? 0.5 : 1,
                                pointerEvents: buyingPackId ? 'none' : 'auto'
                            }}
                                onClick={() => handleBuyCredits(pack.id)}
                            >
                                {pack.bonus && (
                                    <div style={{
                                        position: 'absolute',
                                        top: '18px',
                                        right: '-35px',
                                        background: '#10b981',
                                        color: 'white',
                                        fontSize: '0.75rem',
                                        padding: '4px 40px',
                                        transform: 'rotate(45deg)',
                                        fontWeight: 'bold',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        {pack.bonus}
                                    </div>
                                )}
                                <div style={{ fontSize: '2rem', marginBottom: '10px' }}>🪙</div>
                                <h3 style={{ fontSize: '1.2rem', margin: '0 0 5px 0' }}>{pack.credits.toLocaleString()} Credits</h3>
                                <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#3b82f6', marginBottom: '15px' }}>{pack.price_display}</div>
                                <button
                                    className="btn-secondary"
                                    style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                                    disabled={!!buyingPackId}
                                >
                                    {isLoading ? (
                                        <>
                                            <span className="loading-spinner" style={{
                                                width: '16px',
                                                height: '16px',
                                                border: '2px solid rgba(255,255,255,0.3)',
                                                borderTop: '2px solid white',
                                                borderRadius: '50%',
                                                animation: 'spin 1s linear infinite'
                                            }} />
                                            Opening Checkout...
                                        </>
                                    ) : 'Buy Now'}
                                </button>
                            </div>
                        );
                    })}
                </div>
            </section>

            <section className="settings-section">
                <h2>Profile</h2>
                <div className="settings-card">
                    <div className="form-group">
                        <label>Email</label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <input type="email" value={profile.user.email} disabled style={{ flex: 1 }} />
                            <button
                                className="btn-secondary"
                                onClick={() => {
                                    setShowEmailModal(true);
                                    setEmailError('');
                                    setEmailSuccess('');
                                }}
                                style={{ minWidth: '160px' }}
                            >
                                Update Email
                            </button>
                        </div>
                        {emailSuccess && <p className="success" style={{ color: 'green', marginTop: '10px' }}>{emailSuccess}</p>}
                    </div>

                    <div className="form-group" style={{ marginTop: '20px' }}>
                        <label>Password</label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <input type="password" value="••••••••" disabled style={{ flex: 1 }} />
                            <button
                                className="btn-secondary"
                                onClick={() => {
                                    setShowPasswordModal(true);
                                    setPasswordError('');
                                    setPasswordSuccess('');
                                }}
                                style={{ minWidth: '160px' }}
                            >
                                Change Password
                            </button>
                        </div>
                        {passwordSuccess && <p className="success" style={{ color: 'green', marginTop: '10px' }}>{passwordSuccess}</p>}
                    </div>
                </div>
            </section>

            {/* Email Change Modal */}
            {showEmailModal && (
                <div className="modal-overlay" onClick={() => setShowEmailModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h2>Change Email Address</h2>
                        <p style={{ marginBottom: '20px', color: '#888' }}>
                            You'll receive confirmation emails to both your current and new email addresses.
                        </p>
                        <form onSubmit={handleEmailChange}>
                            <div className="form-group">
                                <label>New Email Address</label>
                                <input
                                    type="email"
                                    value={newEmail}
                                    onChange={(e) => setNewEmail(e.target.value)}
                                    placeholder="Enter new email address"
                                    disabled={emailLoading}
                                    autoFocus
                                />
                            </div>
                            {emailError && <p className="error" style={{ marginTop: '10px' }}>{emailError}</p>}
                            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                                <button
                                    type="submit"
                                    className="btn-primary"
                                    disabled={emailLoading || !newEmail}
                                    style={{ flex: 1 }}
                                >
                                    {emailLoading ? 'Updating...' : 'Update Email'}
                                </button>
                                <button
                                    type="button"
                                    className="btn-secondary"
                                    onClick={() => {
                                        setShowEmailModal(false);
                                        setNewEmail('');
                                        setEmailError('');
                                    }}
                                    disabled={emailLoading}
                                    style={{ flex: 1 }}
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Password Change Modal */}
            {showPasswordModal && (
                <div className="modal-overlay" onClick={() => setShowPasswordModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h2>Change Password</h2>
                        <p style={{ marginBottom: '20px', color: '#888' }}>
                            Enter your new password below. It must be at least 6 characters long.
                        </p>
                        <form onSubmit={handlePasswordChange}>
                            <div className="form-group">
                                <label>New Password</label>
                                <input
                                    type="password"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    placeholder="Enter new password"
                                    disabled={passwordLoading}
                                    autoFocus
                                />
                            </div>
                            <div className="form-group" style={{ marginTop: '15px' }}>
                                <label>Confirm New Password</label>
                                <input
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    placeholder="Confirm new password"
                                    disabled={passwordLoading}
                                />
                            </div>
                            {passwordError && <p className="error" style={{ marginTop: '10px' }}>{passwordError}</p>}
                            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                                <button
                                    type="submit"
                                    className="btn-primary"
                                    disabled={passwordLoading || !newPassword || !confirmPassword}
                                    style={{ flex: 1 }}
                                >
                                    {passwordLoading ? 'Updating...' : 'Update Password'}
                                </button>
                                <button
                                    type="button"
                                    className="btn-secondary"
                                    onClick={() => {
                                        setShowPasswordModal(false);
                                        setNewPassword('');
                                        setConfirmPassword('');
                                        setPasswordError('');
                                    }}
                                    disabled={passwordLoading}
                                    style={{ flex: 1 }}
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <section className="settings-section">
                <h2>Subscription & Billing</h2>
                <div className="settings-card billing-header-card">
                    <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                            <strong style={{ fontSize: '1.2rem' }}>Current Plan: {profile.usage.tier_label}</strong>
                            {profile.usage.tier !== 'free' && (
                                <span className={`badge ${profile.usage.plan_interval === 'year' ? 'badge-annual' : 'badge-monthly'}`}>
                                    {profile.usage.plan_interval === 'year' ? 'Annual' : 'Monthly'}
                                </span>
                            )}
                        </div>

                        {currentTierData && (
                            <div style={{ marginBottom: '15px', color: '#ccc' }}>
                                {currentTierData.description}
                            </div>
                        )}

                        <div className="plan-features" style={{ margin: 0, padding: 0, listStyle: 'none', width: '100%' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>

                                {/* Detailed Perks from TIER_DETAILS */}
                                {currentTierPerks.map((perk, idx) => (
                                    <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                                        <span style={{ color: '#fff', fontWeight: 'bold', lineHeight: '1.4' }}>✓</span>
                                        <span style={{ lineHeight: '1.4', color: '#e1e3ff', fontSize: '0.9rem', flex: 1 }}>
                                            {perk.text}
                                        </span>
                                        {perk.tooltip && (
                                            <div className="tooltip-container" style={{ marginLeft: 'auto' }}>
                                                <Info size={14} className="tooltip-icon" />
                                                <div className="tooltip-content">
                                                    {perk.tooltip}
                                                    <div className="tooltip-arrow"></div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div style={{ marginTop: '15px', display: 'flex', alignItems: 'center', gap: '15px' }}>
                            <p className="sm-text" style={{ margin: 0 }}>
                                {profile.usage.tier === 'free' ? 'Resets on' : 'Next payment:'} {new Date(profile.usage.resets_at).toLocaleDateString()}
                            </p>
                            {profile.usage.stripe_customer_id && (
                                <button
                                    className="link-button"
                                    onClick={handleManageSubscription}
                                    style={{ fontSize: '13px', color: '#666', textDecoration: 'underline', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                                >
                                    View Payment History
                                </button>
                            )}
                        </div>
                    </div>
                    {profile.usage.stripe_customer_id && (
                        <button
                            onClick={handleManageSubscription}
                            disabled={portalLoading}
                            className="btn-secondary"
                        >
                            {portalLoading ? 'Loading...' : 'Manage Subscription'}
                        </button>
                    )}
                </div>

                <div className="billing-toggle-container" style={{ marginTop: 20, marginBottom: 20 }}>
                    <span className={`toggle-label ${billingInterval === 'month' ? 'active' : ''}`}>Monthly</span>
                    <button
                        type="button"
                        className={`toggle-switch ${billingInterval === 'year' ? 'checked' : ''}`}
                        onClick={() => setBillingInterval(prev => prev === 'month' ? 'year' : 'month')}
                    >
                        <span className="toggle-thumb" />
                    </button>
                    <span className={`toggle-label ${billingInterval === 'year' ? 'active' : ''}`}>
                        Annual <span className="discount-badge">Save 20%</span>
                    </span>
                </div>

                <PlanCards
                    tiers={availableTiers}
                    currentTier={profile.usage.tier}
                    currentTierRank={tiers?.[profile.usage.tier]?.rank}
                    onUpgrade={handleUpgrade}
                    onDowngrade={handleDowngrade}
                    billingInterval={billingInterval}
                />

                {/* Credit Usage Comparison Table */}
                <div style={{ marginTop: '40px' }}>
                    <h3 style={{ marginBottom: '20px', fontSize: '1.3rem' }}>Monthly Credit Usage by Tool</h3>
                    <div className="settings-card" style={{
                        overflowX: 'auto',
                        background: 'linear-gradient(135deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 50%, rgba(255,255,255,0.04) 100%)',
                        backdropFilter: 'blur(12px)',
                        WebkitBackdropFilter: 'blur(12px)',
                        border: '1px solid rgba(255,255,255,0.12)',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.08)'
                    }}>
                        <table style={{
                            width: '100%',
                            borderCollapse: 'collapse',
                            fontSize: '0.9rem',
                            minWidth: '600px'
                        }}>
                            <thead>
                                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.15)' }}>
                                    <th style={{ textAlign: 'left', padding: '15px', color: '#888', fontWeight: '500' }}>Tool</th>
                                    <th style={{ textAlign: 'center', padding: '15px', color: '#888', fontWeight: '500' }}>Credits</th>
                                    <th style={{ textAlign: 'center', padding: '15px', color: '#aaa', fontWeight: '600' }}>Free<br /><span style={{ fontSize: '0.75rem', color: '#666' }}>50/mo</span></th>
                                    <th style={{ textAlign: 'center', padding: '15px', color: '#aaa', fontWeight: '600' }}>Creator<br /><span style={{ fontSize: '0.75rem', color: '#666' }}>1,100/mo</span></th>
                                    <th style={{ textAlign: 'center', padding: '15px', color: '#aaa', fontWeight: '600' }}>Pro<br /><span style={{ fontSize: '0.75rem', color: '#666' }}>3,000/mo</span></th>
                                    <th style={{ textAlign: 'center', padding: '15px', color: '#aaa', fontWeight: '600' }}>Studio<br /><span style={{ fontSize: '0.75rem', color: '#666' }}>6,500/mo</span></th>
                                </tr>
                            </thead>
                            <tbody>
                                {[
                                    { tool: 'Rant Video', credits: 30, free: 1, creator: 36, pro: 100, studio: 216 },
                                    { tool: 'Quiz Video', credits: 10, free: 5, creator: 110, pro: 300, studio: 650 },
                                    { tool: 'Ranked Export', credits: 5, free: 10, creator: 220, pro: 600, studio: 1300 },
                                    { tool: 'Smart Clips', credits: 2, free: '25 min', creator: '550 min', pro: '1,500 min', studio: '3,250 min' },
                                    { tool: 'Downloader', credits: 0, free: '∞', creator: '∞', pro: '∞', studio: '∞' },
                                    { tool: 'Audio Converter', credits: 0, free: '∞', creator: '∞', pro: '∞', studio: '∞' },
                                    { tool: 'Video Splitter', credits: 0, free: '∞', creator: '∞', pro: '∞', studio: '∞' },
                                ].map((row, idx) => (
                                    <tr key={idx} style={{
                                        borderBottom: '1px solid rgba(255,255,255,0.08)',
                                        background: idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)'
                                    }}>
                                        <td style={{ textAlign: 'left', padding: '14px 15px' }}>
                                            {row.tool}
                                        </td>
                                        <td style={{ textAlign: 'center', padding: '14px 15px', color: row.credits === 0 ? '#10b981' : '#888' }}>
                                            {row.credits === 0 ? 'Free' : row.credits}
                                        </td>
                                        <td style={{ textAlign: 'center', padding: '14px 15px', color: '#fff' }}>{row.free}</td>
                                        <td style={{ textAlign: 'center', padding: '14px 15px', color: '#fff' }}>{row.creator}</td>
                                        <td style={{ textAlign: 'center', padding: '14px 15px', color: '#fff' }}>{row.pro}</td>
                                        <td style={{ textAlign: 'center', padding: '14px 15px', color: '#fff', fontWeight: '500' }}>{row.studio}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <div style={{ padding: '15px', color: '#666', fontSize: '0.8rem', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                            <strong>Note:</strong> Rant = Script (5) + Voice (20) + Render (5) = 30 credits · Quiz = Generate (5) + Render (5) = 10 credits · Smart Clips charged per minute of source video
                        </div>
                    </div>
                </div>
            </section>

            {/* Plan Change Confirmation Modal */}
            <ConfirmationModal
                isOpen={planModal.isOpen}
                title={
                    planModal.type === 'success'
                        ? 'Downgrade Scheduled!'
                        : planModal.type === 'upgrade'
                            ? `Upgrade to ${planModal.tierName}?`
                            : `Downgrade to ${planModal.tierName}?`
                }
                message={
                    planModal.type === 'success'
                        ? planModal.successMessage
                        : planModal.type === 'upgrade'
                            ? `You will be charged ${planModal.priceDisplay}/${planModal.intervalLabel} starting today.\n\nYour subscription will renew on ${planModal.nextBillingDate} at this new rate.`
                            : `• You will NOT be charged today.\n• Your current plan remains active until ${planModal.nextBillingDate}.\n• Starting ${planModal.nextBillingDate}, you will be charged ${planModal.priceDisplay}/${planModal.intervalLabel}.`
                }
                confirmText={planModal.type === 'success' ? 'Got it' : planModal.type === 'upgrade' ? 'Continue to Checkout' : 'Confirm Downgrade'}
                cancelText="Cancel"
                onConfirm={confirmPlanChange}
                onCancel={closePlanModal}
            />
        </div>
    );
};

